import React, { useState } from "react";
import { Box } from "@mui/material";
import "../../styles/scss/DataTable.scss";

export interface Column {
  id: string;
  label: string;
  align?: "left" | "right" | "center";
  minWidth?: number;
  sortable?: boolean;
  format?: (value: any, row: any, index?: number) => React.ReactNode;
}

interface DataTableProps {
  columns: Column[];
  rows: any[];
  title?: string;
  onRowClick?: (row: any) => void;
  rowsPerPageOptions?: number[];
  totalCount: number;
  onPaginationChange?: (page: number, rowsPerPage: number) => void;
  page?: number;
  limit?: number;
  renderChildRow?: (row: any) => React.ReactNode;
  expandedRow?: number;
  selectable?: boolean;
}

export default function DataTable({
  columns,
  rows,
  title,
  onRowClick,
  rowsPerPageOptions = [10, 25, 50, 100],
  totalCount,
  onPaginationChange,
  page = 0,
  limit = 25,
  renderChildRow,
  expandedRow,
  selectable = false,
}: DataTableProps) {
  const [currentPage, setCurrentPage] = useState(page);
  const [rowsPerPage, setRowsPerPage] = useState(limit);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setCurrentPage(newPage);
    onPaginationChange?.(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(0);
    onPaginationChange?.(0, newRowsPerPage);
  };

  const getColumnClass = (align?: string) => {
    if (align === 'center') return 'center';
    if (align === 'right') return 'right';
    return '';
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const currentPageNum = currentPage + 1;

  const renderPaginationNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPageNum <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPageNum >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <Box className="data-table-container">
      {title && (
        <Box className="data-table-header">
          <Box className="data-table-title">{title}</Box>
        </Box>
      )}

      <Box className="data-table-wrapper">
        <table className="data-table">
          <thead className="table-head">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={`${getColumnClass(column.align)} ${column.sortable ? 'sortable' : ''}`}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="table-body">
            {rows.map((row, index) => (
              <React.Fragment key={index}>
                <tr
                  className={`data-grid-row ${index % 2 === 1 ? 'alternate' : ''}`}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((column) => (
                    <td key={column.id} className={getColumnClass(column.align)}>
                      {column.format
                        ? column.format(row[column.id], row, index)
                        : row[column.id]}
                    </td>
                  ))}
                </tr>
                {expandedRow === index && renderChildRow && (
                  <tr className="expanded-row">
                    <td colSpan={columns.length}>
                      {renderChildRow(row)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {rows.length === 0 && (
          <Box className="table-empty">
            <span className="material-symbols-outlined">inbox</span>
            <p>No data available</p>
          </Box>
        )}
      </Box>

      {totalCount > 0 && (
        <Box className="table-pagination">
          <Box className="pagination-info">
            <span>{totalCount}</span> records
            <select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option} / page
                </option>
              ))}
            </select>
          </Box>

          <Box className="pagination-controls">
            <button
              className="pagination-btn nav"
              onClick={() => handleChangePage(null, 0)}
              disabled={currentPage === 0}
            >
              <span className="material-symbols-outlined">first_page</span>
            </button>
            <button
              className="pagination-btn nav"
              onClick={() => handleChangePage(null, currentPage - 1)}
              disabled={currentPage === 0}
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </button>

            {renderPaginationNumbers().map((pageNum, idx) => (
              pageNum === '...' ? (
                <span key={idx} className="pagination-ellipsis">...</span>
              ) : (
                <button
                  key={idx}
                  className={`pagination-btn ${currentPageNum === pageNum ? 'active' : ''}`}
                  onClick={() => handleChangePage(null, (pageNum as number) - 1)}
                >
                  {pageNum}
                </button>
              )
            ))}

            <button
              className="pagination-btn nav"
              onClick={() => handleChangePage(null, currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
            <button
              className="pagination-btn nav"
              onClick={() => handleChangePage(null, totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
            >
              <span className="material-symbols-outlined">last_page</span>
            </button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
